import React from 'react';
import { useTranslation } from 'react-i18next';
import { DeliveryHourSelect } from './DeliveryHourSelect';
import { MonthYearPicker } from '../ui/MonthYearPicker';
import type { FormDataState } from '../../hooks/useCreateQuote';

interface DateSelectionSectionProps {
  formData: FormDataState;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleCountryChange: (name: string, value: string) => void;
  inputClass: string;
  labelClass: string;
}

export const DateSelectionSection: React.FC<DateSelectionSectionProps> = ({
  formData,
  handleChange,
  handleCountryChange,
  inputClass,
  labelClass
}) => {
  const { t } = useTranslation();
  const sectionTitleClass = "text-lg leading-none tracking-tight mb-4 text-foreground";

  return (
    <div className="space-y-4">
      <h4 className={sectionTitleClass}>{t('quote.form.sections.date_time')}</h4>
      <div className="flex space-x-4 mb-4">
        {['fixed', 'flexible', 'none'].map((type) => (
          <label key={type} className={`flex items-center space-x-2 border rounded-md p-3 cursor-pointer transition-colors ${formData.date_type === type ? 'bg-accent text-accent-foreground border-ring' : 'hover:bg-accent/50'}`}>
            <input
              type="radio"
              name="date_type"
              value={type}
              checked={formData.date_type === type}
              onChange={handleChange}
              className="h-4 w-4 text-foreground focus:ring-ring"
            />
            <span className="capitalize text-sm font-medium">{t(`quote.form.fields.date_type.${type}`)}</span>
          </label>
        ))}
      </div>

      {formData.date_type === 'fixed' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <p className="text-sm font-medium text-muted-foreground">{t('quote.form.fields.schedules.origin')}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>{t('quote.form.fields.schedules.date')}<span className="text-red-500 ml-1">*</span></label>
                <input type="date" name="origin_fixed_date" className={inputClass} value={formData.origin_fixed_date} onChange={handleChange} />
              </div>
              <div>
                <label className={labelClass}>{t('quote.form.fields.schedules.loading_hour')}</label>
                <DeliveryHourSelect
                  value={formData.origin_fixed_hour}
                  onChange={(hour) => handleCountryChange('origin_fixed_hour', hour)}
                  placeholder={t('quote.form.fields.schedules.loading_hour')}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <p className="text-sm font-medium text-muted-foreground">{t('quote.form.fields.schedules.destination')}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>{t('quote.form.fields.schedules.date')}<span className="text-red-500 ml-1">*</span></label>
                <input type="date" name="destination_fixed_date" className={inputClass} value={formData.destination_fixed_date} onChange={handleChange} />
              </div>
              <div>
                <label className={labelClass}>{t('quote.form.fields.schedules.delivery_hour')}</label>
                <DeliveryHourSelect
                  value={formData.destination_fixed_hour}
                  onChange={(hour) => handleCountryChange('destination_fixed_hour', hour)}
                  placeholder={t('quote.form.fields.schedules.delivery_hour')}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {formData.date_type === 'flexible' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <p className="text-sm font-medium text-muted-foreground">{t('quote.form.fields.ranges.origin')}</p>
            <div>
              <label className={labelClass}>{t('quote.form.fields.ranges.start_date')}<span className="text-red-500 ml-1">*</span></label>
              <MonthYearPicker
                name="origin_date"
                value={formData.origin_date}
                onChange={(value) => handleCountryChange('origin_date', value)}
                className={inputClass}
              />
            </div>
          </div>
          <div className="space-y-4">
            <p className="text-sm font-medium text-muted-foreground">{t('quote.form.fields.ranges.destination')}</p>
            <div>
              <label className={labelClass}>{t('quote.form.fields.ranges.end_date')}<span className="text-red-500 ml-1">*</span></label>
              <MonthYearPicker
                name="destination_date"
                value={formData.destination_date}
                onChange={(value) => handleCountryChange('destination_date', value)}
                className={inputClass}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
